import {
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

// helper
import { checkPermission } from "../../helper/PermissionHelper";
import { pageName } from "../../reducers/commonSlice";
import { user } from "../../reducers/userSlice";

// icons
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";

// components
import DebugLogger from "../../components/DebugLogger";
import TableSkeleton from "../../components/TableSkeleton";

// api
import { RoleApi } from "../../api/RoleApi";
import { UserApi } from "../../api/UserApi";

export default function List() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filter, setFilter] = useState();
  const [rolePage, setRolePage] = useState(1);
  const [rolePageSize, setRolePageSize] = useState(-1);
  const [roleFilter, setRoleFilter] = useState([]);
  const [searchStr, setSearchStr] = useState("");
  const [total, setTotal] = useState(0);
  const [data, setData] = useState([]);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const [haveCreatePermission, setHaveCreatePermission] = useState(false);
  const [haveUpdatePermission, setHaveUpdatePermission] = useState(false);
  const currentUser = useSelector(user);
  const currentPage = useSelector(pageName);
  const [colSpan, setColSpan] = useState(9);

  useEffect(() => {
    if (searchParams.get("page")) {
      setPage(parseInt(searchParams.get("page")));
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData();
  }, [page]);

  useEffect(() => {
    const createPermission = checkPermission(currentUser, currentPage, "create");
    setHaveCreatePermission(createPermission);

    const updatePermission = checkPermission(currentUser, currentPage, "update");
    setHaveUpdatePermission(updatePermission);
  }, [currentUser, currentPage]);

  useEffect(() => {
    if (!filter || filter.length <= 0) return;
    setPage(1);
    fetchData();
  }, [filter]);

  useEffect(() => {
    fetchRoleData();
  }, []);

  const fetchRoleData = async () => {
    try {

      const response = await RoleApi.list(rolePage, rolePageSize, roleFilter);
      if (response) {
        setOptions(response.data);
      }

    } catch (error) {
      console.log(error);
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await UserApi.list(page, pageSize, filter);
      if (response) {
        setData(response.data);
        setTotal(response.total);
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const viewDetail = id => {
    navigate(`/users/${id}`);
  };

  const newUser = () => {
    navigate(`/users/create`);
  };

  const onChangePage = value => {
    setPage(value);
    setSearchParams({ page: value });
  };

  const onChangeSearchField = value => {
    setSearchStr(value);
  };

  const search = () => {
    setPage(1);
    setFilter([
      {
        field: "username, email, phone_number",
        value: searchStr,
        op: "like",
      },
    ]);
  };

  const getRoleLable = roles => {
    if (!options || options.length === 0) return "";
    let result =  roles.map(role => {
      const foundOption = options.find(option => option.id === role);

      return foundOption ? foundOption.name : "";
    });

    return result.join(", ");
  };

  return (
    <Container maxWidth="false">
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to={"/settings"}>{t("menu.settings")}</Link>
        <Typography color="text.primary">{t("menu.users")}</Typography>
      </Breadcrumbs>
      <Card variant="outlined">
        <CardHeader
          title={t("menu.users")}
          action={
            haveCreatePermission && (
              <Button color="inherit" variant="outlined" startIcon={<AddIcon />} onClick={newUser}>
                {t("user.new_user")}
              </Button>
            )
          }
        />

        <CardContent>
          <DebugLogger title={"Users"} data={data} hidden={true} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              pb: 1,
            }}
          >
            <TextField
              placeholder={t("layout.search_by_username_or_phone_or_email")}
              onChange={event => {
                onChangeSearchField(event.target.value);
              }}
              onKeyDown={event => {
                if (event.key == "Enter") {
                  search();
                }
              }}
              sx={{
                width: "35%",
              }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    edge="end"
                    onClick={() => {
                      search();
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>
          <TableContainer elevation={0} component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell width={"5%"}>{t("user.id")}</TableCell>
                  <TableCell width={"5%"}>{t("user.icons")}</TableCell>
                  <TableCell width={"10%"}>{t("user.username")}</TableCell>
                  <TableCell width={"10%"}>{t("user.name_zh")}</TableCell>
                  <TableCell width={"10%"}>{t("user.name_en")}</TableCell>
                  <TableCell width={"10%"}>{t("user.rank")}</TableCell>
                  <TableCell width={"10%"}>{t("user.email")}</TableCell>
                  <TableCell width={"10%"}>{t("user.phone_number")}</TableCell>
                  <TableCell width={"15%"}>{t("user.role")}</TableCell>
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableSkeleton row={pageSize} colSpan={colSpan} />
              ) : (
                <TableBody>
                  {!data.length && (
                    <TableRow>
                      <TableCell colSpan={colSpan} align="center">
                        {t("layout.no_data")}
                      </TableCell>
                    </TableRow>
                  )}
                  {data.map(row => (
                    <TableRow
                      hover
                      key={row.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      onClick={() =>
                        haveUpdatePermission
                          ? viewDetail(row.id)
                          : toast.error(t("error.no_update_permission"))
                      }
                    >
                      <TableCell component="th" scope="row">
                        {row.id}
                      </TableCell>
                      <TableCell>
                        <Avatar
                          sx={{ border: "1px solid #0000001f" }}
                          alt={`user_icon_${row.avatarId}`}
                          src={`/assets/avatar/${row.avatarId}.png`}
                        />
                      </TableCell>
                      <TableCell>{row.username}</TableCell>
                      <TableCell>{row.nameZh}</TableCell>
                      <TableCell>{row.nameEn}</TableCell>
                      <TableCell>{row.rank}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.phoneNumber}</TableCell>
                      <TableCell>{getRoleLable(row.roles)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </CardContent>

        <CardActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Typography variant="body2">
            {t("layout.total")}: {total}
          </Typography>
          <Stack spacing={2}>
            <Pagination
              page={page}
              count={Math.ceil(total / pageSize)}
              shape="rounded"
              onChange={(event, value) => {
                onChangePage(value);
              }}
            />
          </Stack>
        </CardActions>
      </Card>
    </Container>
  );
}
